import React, { useState, useEffect } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { AlertCircle, Loader2 } from 'lucide-react'
import Modal from './Modal'
import { api } from '../lib/api'

interface EditFeedModalProps {
  isOpen: boolean
  onClose: () => void
  feed: any
  collections: Array<{ id: string; title: string }>
}

interface FeedForm {
  name: string
  taxii_url: string
  collection_id: string
  poll_interval_minutes: number
  auth_type: 'none' | 'basic' | 'bearer'
  username: string
  password: string
  bearer_token: string
  is_active: boolean
}

export default function EditFeedModal({ isOpen, onClose, feed, collections }: EditFeedModalProps) {
  const [form, setForm] = useState<FeedForm>({
    name: '',
    taxii_url: '',
    collection_id: '',
    poll_interval_minutes: 60,
    auth_type: 'none',
    username: '',
    password: '',
    bearer_token: '',
    is_active: true
  })
  const [error, setError] = useState('')

  const queryClient = useQueryClient()

  // Populate form when feed prop changes
  useEffect(() => {
    if (feed) {
      let authConfig = null
      try {
        authConfig = feed.auth_config ? JSON.parse(feed.auth_config) : null
      } catch (e) {
        console.warn('Failed to parse auth config:', e)
      }

      setForm({
        name: feed.name || '',
        taxii_url: feed.taxii_url || '',
        collection_id: feed.collection_id || '',
        poll_interval_minutes: feed.poll_interval_minutes || 60,
        auth_type: feed.auth_type || 'none',
        username: authConfig?.username || '',
        password: '', // Never populate password for security
        bearer_token: '', // Never populate token for security
        is_active: feed.is_active ?? true
      })
    }
  }, [feed])

  const updateFeedMutation = useMutation({
    mutationFn: async (feedData: FeedForm) => {
      // Prepare auth config based on auth type
      let auth_config = null
      if (feedData.auth_type === 'basic') {
        auth_config = {
          type: 'basic',
          username: feedData.username,
          password: feedData.password || undefined // Only update if provided
        }
      } else if (feedData.auth_type === 'bearer') {
        auth_config = {
          type: 'bearer',
          token: feedData.bearer_token || undefined // Only update if provided
        }
      }

      const payload = {
        name: feedData.name,
        taxii_url: feedData.taxii_url,
        collection_id: feedData.collection_id,
        poll_interval_minutes: feedData.poll_interval_minutes,
        auth_type: feedData.auth_type,
        auth_config: auth_config ? JSON.stringify(auth_config) : null,
        is_active: feedData.is_active
      }

      return api.updateFeed(feed.id, payload)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['feeds'] })
      onClose()
      setError('')
    },
    onError: (error: Error) => {
      setError(error.message)
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // Basic validation
    if (!form.name.trim()) {
      setError('Feed name is required')
      return
    }

    if (!form.taxii_url.trim()) {
      setError('TAXII URL is required')
      return
    }

    if (!form.collection_id) {
      setError('Please select a collection')
      return
    }

    // Validate URL format
    try {
      new URL(form.taxii_url)
    } catch {
      setError('Please enter a valid URL')
      return
    }

    updateFeedMutation.mutate(form)
  }

  const handleClose = () => {
    if (!updateFeedMutation.isPending) {
      setError('')
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Edit Feed" size="lg">
      <form onSubmit={handleSubmit} className="space-y-6">
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error</h3>
                <div className="mt-2 text-sm text-red-700">{error}</div>
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <label htmlFor="name" className="block text-sm font-medium text-gray-700">
              Feed Name *
            </label>
            <input
              type="text"
              id="name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          <div className="md:col-span-2">
            <label htmlFor="taxii_url" className="block text-sm font-medium text-gray-700">
              TAXII URL *
            </label>
            <input
              type="url"
              id="taxii_url"
              value={form.taxii_url}
              onChange={(e) => setForm({ ...form, taxii_url: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              required
            />
          </div>

          <div>
            <label htmlFor="collection_id" className="block text-sm font-medium text-gray-700">
              Target Collection *
            </label>
            <select
              id="collection_id"
              value={form.collection_id}
              onChange={(e) => setForm({ ...form, collection_id: e.target.value })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
              required
            >
              <option value="">Select a collection</option>
              {collections.map((collection) => (
                <option key={collection.id} value={collection.id}>
                  {collection.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="poll_interval" className="block text-sm font-medium text-gray-700">
              Poll Interval (minutes)
            </label>
            <input
              type="number"
              id="poll_interval"
              min="1"
              max="1440"
              value={form.poll_interval_minutes}
              onChange={(e) => setForm({ ...form, poll_interval_minutes: parseInt(e.target.value) || 60 })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="md:col-span-2">
            <div className="flex items-center">
              <input
                id="is_active"
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
                Feed is active
              </label>
            </div>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="auth_type" className="block text-sm font-medium text-gray-700">
              Authentication
            </label>
            <select
              id="auth_type"
              value={form.auth_type}
              onChange={(e) => setForm({ ...form, auth_type: e.target.value as any })}
              className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="none">No Authentication</option>
              <option value="basic">Basic Auth (Username/Password)</option>
              <option value="bearer">Bearer Token</option>
            </select>
          </div>

          {form.auth_type === 'basic' && (
            <>
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                  <span className="text-xs text-gray-500 ml-1">(leave blank to keep existing)</span>
                </label>
                <input
                  type="password"
                  id="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Enter new password or leave blank"
                />
              </div>
            </>
          )}

          {form.auth_type === 'bearer' && (
            <div className="md:col-span-2">
              <label htmlFor="bearer_token" className="block text-sm font-medium text-gray-700">
                Bearer Token
                <span className="text-xs text-gray-500 ml-1">(leave blank to keep existing)</span>
              </label>
              <input
                type="password"
                id="bearer_token"
                value={form.bearer_token}
                onChange={(e) => setForm({ ...form, bearer_token: e.target.value })}
                className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="Enter new token or leave blank"
              />
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={updateFeedMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={updateFeedMutation.isPending}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 disabled:opacity-50 flex items-center"
          >
            {updateFeedMutation.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {updateFeedMutation.isPending ? 'Updating...' : 'Update Feed'}
          </button>
        </div>
      </form>
    </Modal>
  )
}